import { Component, HostListener, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ConfirmDialogService } from '@serviceplan/ui-kit';
import { AssetUploadService } from '@thehubz/client/assets-module';
import { ClientEnvironment, ClientNotificationHandlerService, ENVIRONMENT, FileObject, UserSessionService } from '@thehubz/client/core';
import { User, UserInfo, UsersApiService } from '@thehubz/client/data-access-api';
import { FileUploadService } from '@thehubz/client/file-upload';
import { CustomClients } from '@thehubz/shared/branding';
import { Subscription } from 'rxjs';
import { delay, tap } from 'rxjs/operators';

@Component({
  selector: 'thehubz-user-avatar',
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
})
export class UserAvatarComponent implements OnInit, OnChanges {
  @Input() userData!: User;
  @Input() isOtherUser = false;
  @Input() userId?: string;

  spUser!: UserInfo;
  uploadStartSubscription!: Subscription;
  uploadCompleteSubscription!: Subscription;
  avatarSize = 100;
  avatarFontSize = 30;
  showNameToolTip = true;
  files: FileObject[] = [];
  fileLength = 0;
  uploadInProgress = false;
  disableUpload = false;

  brandingKey!: CustomClients;
  CustomClients = CustomClients;

  /* To prevent window close if uploads are in progress in upload asset dialog */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload() {
    if (this.uploadInProgress) {
      return false;
    }
    return true;
  }

  constructor(
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private fileUploadService: FileUploadService,
    private confirmService: ConfirmDialogService,
    private userSessionService: UserSessionService,
    private userService: UsersApiService,
    private assetUploadService: AssetUploadService,
    @Inject(ENVIRONMENT) env: ClientEnvironment,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    this.fileUploadsSubscription();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.userData) {
      this.spUser = {
        id: this.userData._id,
        name: this.userData.firstName + ' ' + this.userData.lastName,
        email: this.userData.email,
      };
    }
  }

  fileBrowseHandler(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.prepareFilesList(target.files);
      target.value = '';
    }
  }

  private prepareFilesList(files: FileList) {
    this.files = [];
    for (const item of files) {
      if (this.files.find((fileObj: FileObject) => fileObj.name === item.name)) {
        this.clientNotificationHandlerService.successNotification(`File with name ${item.name} already selected`, 'info');
      }
      this.files.push({ file: item, key: item.name, name: item.name, uploaded: false, uploading: false });
      this.fileLength = this.files.length;
    }
    this.startUpload();
  }

  private async startUpload() {
    this.uploadInProgress = true;
    this.disableUpload = true;
    try {
      try {
        await this.fileUploadService.deleteUserFile(this.userData._id);
      } catch (err) {
        // empty
      }

      for await (const fileObj of this.files) {
        if (!fileObj.uploaded) {
          const incompleteUpload = await this.assetUploadService.checkForIncompleteUploads(fileObj, this.files);
          if (typeof incompleteUpload === 'boolean') {
            const key = `users/${this.userData._id}/profile.${fileObj.name.split('.').pop()}`;
            const fileIndex = this.files.findIndex((fileOb) => fileOb.name === fileObj.name);
            const file = this.files[fileIndex];
            this.files[fileIndex] = { ...file, key: key };
            await this.assetUploadService.uploadFile({ key: key, file: fileObj.file });
          } else {
            await this.assetUploadService.uploadFile(incompleteUpload);
          }
        }
      }
    } catch (err) {
      this.onError(err);
    }
  }

  private onError(err: unknown) {
    this.uploadInProgress = false;
    this.clientNotificationHandlerService.errorNotification(err);
  }

  private fileUploadsSubscription(): void {
    this.uploadCompleteSubscription = this.fileUploadService.uploadedFileSubject
      .pipe(
        tap(() => {
          this.clientNotificationHandlerService.successNotification($localize`processing uploaded image ... please wait ...`);
        }),
        delay(5000),
      )
      .subscribe({
        next: () => {
          this.uploadInProgress = false;
          // reload page ...
          window.location.reload();
        },
        error: this.onError,
      });
  }

  deleteUserAccount() {
    const userId = this.userData._id;
    this.confirmService
      .open({
        title: $localize`Delete user`,
        message: $localize`Are you sure you want to delete your account permanently?`,
      })
      .subscribe((res) => {
        if (res) {
          this.userService.usersControllerRemove(userId).subscribe({
            next: (data) => {
              if (data) {
                this.clientNotificationHandlerService.successNotification($localize`Your account has been deleted successfully`);
                setTimeout(() => {
                  this.userSessionService.logoutUser(true);
                }, 1000);
              }
            },
            error: this.onError,
          });
        }
      });
  }
}
